import { PositionType } from '@interfaces';
import { MarginCode, OptionPermission, SecurityType } from '@interfaces/models';

export const optionsContractMultiplier = 100;

export const optionSecurityTypes: readonly SecurityType[] = [
  SecurityType.CALL_OPTION,
  SecurityType.PUT_OPTION,
  SecurityType.BANK_CALL_OPTION,
];

export const isOption = (securityType: SecurityType | null | undefined): boolean => {
  if (securityType == null) return false;
  return optionSecurityTypes.includes(securityType);
};

export const hasOptionsHoldings = (holdings: { securityType: SecurityType }[]): boolean =>
  holdings.some((holding) => isOption(holding.securityType));

export const validMoOptionPermissions: ReadonlySet<OptionPermission> = new Set<OptionPermission>([
  OptionPermission.LEVEL_1,
  OptionPermission.LEVEL_2,
  OptionPermission.LEVEL_3,
  OptionPermission.LEVEL_4,
]);

const OPTION_PERMISSION_RANK: Readonly<Partial<Record<OptionPermission, number>>> = {
  [OptionPermission.LEVEL_1]: 1,
  [OptionPermission.LEVEL_2]: 2,
  [OptionPermission.LEVEL_3]: 3,
  [OptionPermission.LEVEL_4]: 4,
};

// Multi-IT amounts page pre-flight: recipient is downgraded when its option permission
// is either not a valid LEVEL_1..LEVEL_4 or ranks strictly below the FROM account's.
export const isRecipientOptionPermissionDowngraded = (
  fromPermission: OptionPermission | null | undefined,
  toPermission: OptionPermission | null | undefined,
): boolean => {
  const fromRank = fromPermission ? OPTION_PERMISSION_RANK[fromPermission] : undefined;
  if (fromRank === undefined) return false;
  const toRank = toPermission ? OPTION_PERMISSION_RANK[toPermission] : undefined;
  if (toRank === undefined) return true;
  return toRank < fromRank;
};

export const hasShortPosition = (
  positions: { positionType?: PositionType | null }[] = [],
): boolean =>
  positions.some(
    (p) =>
      p.positionType === PositionType.SHORT || p.positionType === PositionType.SHORT_MARGIN_ACCOUNT,
  );

// Any margin-approved account is blocked with the "call us" banner.
export const isMarginApproved = (account: { marginCode?: MarginCode }): boolean =>
  account.marginCode === MarginCode.MARGIN;

export const isEligibleMoAccount = (account: {
  marginCode: MarginCode;
  optionPermission: OptionPermission;
}): boolean => {
  return (
    account.marginCode === MarginCode.MARGIN &&
    validMoOptionPermissions.has(account.optionPermission)
  );
};

export const isOptionsAuthorizedAccount = (account: {
  optionPermission: OptionPermission;
}): boolean => validMoOptionPermissions.has(account.optionPermission);

const ET_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export function getEasternOffsetMinutes(year: number, monthIndex: number, day: number): number {
  const noonUTC = Date.UTC(year, monthIndex, day, 12, 0, 0);
  const parts = ET_FORMATTER.formatToParts(noonUTC).reduce<Record<string, number>>((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = parseInt(p.value, 10);
    return acc;
  }, {});
  const noonInET = Date.UTC(
    parts['year'],
    parts['month'] - 1,
    parts['day'],
    parts['hour'],
    parts['minute'],
    parts['second'],
  );
  return Math.round((noonUTC - noonInET) / 60_000);
}

const EXPIRY_PATTERN = /\bEXP (\d{2})\/(\d{2})\/(\d{2})\b/;

interface ParsedExpiry {
  year: number;
  monthIndex: number;
  day: number;
}

// `Date.UTC` silently normalizes out-of-range values (e.g. month 13 → Feb next year). Round-trip check rejects them.
function parseExpiryDate(securityName: string | null | undefined): ParsedExpiry | null {
  const match = securityName?.match(EXPIRY_PATTERN);
  if (!match) return null;

  const year = 2000 + parseInt(match[3], 10);
  const monthIndex = parseInt(match[1], 10) - 1;
  const day = parseInt(match[2], 10);

  const probe = new Date(Date.UTC(year, monthIndex, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== monthIndex ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, monthIndex, day };
}

export function isOptionExpiringWithin7Days(
  securityName: string | null | undefined,
  now: number = Date.now(),
): boolean {
  const parsed = parseExpiryDate(securityName);
  if (!parsed) return false;

  const etOffsetMinutes = getEasternOffsetMinutes(parsed.year, parsed.monthIndex, parsed.day);
  const endOfDayETinUTC =
    Date.UTC(parsed.year, parsed.monthIndex, parsed.day, 23, 59, 59) + etOffsetMinutes * 60_000;

  return endOfDayETinUTC - now < 7 * 24 * 60 * 60 * 1000;
}

export function hasUnrecognizedExpiryFormat(securityName: string | null | undefined): boolean {
  if (!securityName) return false;
  return /\bEXP\b/.test(securityName) && parseExpiryDate(securityName) === null;
}

interface ExpiringOptionPosition {
  securityType?: SecurityType | string | null;
  securityName?: string | null;
}

export function computeExpiringOptionIndexes(
  positions: ExpiringOptionPosition[],
  now: number = Date.now(),
): Set<number> {
  const indexes = new Set<number>();
  positions.forEach((position, index) => {
    if (
      isOption(position.securityType as SecurityType) &&
      isOptionExpiringWithin7Days(position.securityName, now)
    ) {
      indexes.add(index);
    }
  });
  return indexes;
}

interface RestrictedPosition {
  restricted?: boolean;
}

export function computeRestrictedPositionIndexes(positions: RestrictedPosition[]): Set<number> {
  const indexes = new Set<number>();
  positions.forEach((position, index) => {
    if (position.restricted) {
      indexes.add(index);
    }
  });
  return indexes;
}
